import { useEffect } from "react";
import { BUSINESS_NAME, PHONE_DISPLAY } from "@/data/site";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Manages per-page SEO: title, description, canonical, Open Graph, and JSON-LD.
 * `breadcrumbs`: array of { name, path } for BreadcrumbList schema.
 * `faq`: array of { q, a } for FAQPage schema.
 */
export default function Seo({ title, description, path = "/", breadcrumbs, faq }) {
  useEffect(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const url = origin + path;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:image", origin + "/assets/og-image.jpg");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", origin + "/assets/og-image.jpg");

    const business = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: BUSINESS_NAME,
      image: origin + "/assets/og-image.jpg",
      logo: origin + "/assets/icon-512.png",
      url: origin + "/",
      telephone: "+1-336-900-2307",
      areaServed: "Greensboro, NC and surrounding cities and communities",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Greensboro",
        addressRegion: "NC",
        addressCountry: "US",
      },
      description:
        "Residential and commercial cleaning, move-in/move-out cleaning, and junk removal in Greensboro, NC.",
      makesOffer: [
        "Residential Cleaning",
        "Commercial Cleaning",
        "Move-In and Move-Out Cleaning",
        "Junk Removal",
        "Property Cleanouts",
      ].map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s } })),
    };
    setJsonLd("ld-business", business);

    setJsonLd(
      "ld-breadcrumbs",
      breadcrumbs && breadcrumbs.length
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.name,
              item: origin + b.path,
            })),
          }
        : null
    );

    setJsonLd(
      "ld-faq",
      faq && faq.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null
    );
  }, [title, description, path, breadcrumbs, faq]);

  return null;
}

export { PHONE_DISPLAY };
